import React from "react";
import { Box, Typography, Chip, CircularProgress } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import { BADGE_COLORS, getBadgeStyles, getAlmacenColor } from "../components/ui/badgeColors";
import { TipoBadge, EstadoBadge, StockDisplay } from "../components/ui";
import { PETICION_ESTADO, PETICION_TIPO } from "../constants";
import { DATA_GRID_FONT_SIZE } from "../../../components/ui/dataGridStyles";
import DateUtils from "../../../utils/DateUtils";

/**
 * Columnas del DataGrid de "Mis peticiones"
 */
export const generateMisPeticionesColumns = ({ getNombreAlmacen, processingId, onEliminar }) => [

            {
                field: 'id',
                headerName: 'ID',
                width: 60,
                renderCell: (params) => (
                    <Typography sx={{ fontWeight: 600, fontSize: DATA_GRID_FONT_SIZE.CELDA }}>
                        #{params.value}
                    </Typography>
                ),
            },
            {
                field: 'referencia',
                headerName: 'Ref',
                width: 100,
                renderCell: (params) => (
                    <Typography sx={{ fontWeight: 600, fontSize: DATA_GRID_FONT_SIZE.CELDA }}>
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: 'nombreProducto',
                headerName: 'Producto',
                flex: 1,
                minWidth: 120,
            },
            {
                field: 'stock',
                headerName: 'Stock',
                width: 160,
                renderCell: (params) => (
                    <StockDisplay
                        stock={params.row.stock}
                        getNombreAlmacen={getNombreAlmacen}
                        variant="compact"
                        maxVisible={99}
                    />
                ),
            },
            {
                field: 'unidades',
                headerName: 'Uds',
                width: 70,
                type: 'number',
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={getBadgeStyles(BADGE_COLORS.GRAY)}
                    />
                ),
            },
            {
                field: 'numeroAlbaran',
                headerName: 'Albarán',
                width: 100,
                renderCell: (params) => (
                    <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA }}>
                        {params.value || '-'}
                    </Typography>
                ),
            },
            {
                field: 'tipo',
                headerName: 'Tipo',
                width: 110,
                renderCell: (params) => {
                    const tipo = params.value || PETICION_TIPO.PETICION.value;
                    return <TipoBadge tipo={tipo} />;
                }
            },
            {
                field: 'estado',
                headerName: 'Estado',
                width: 120,
                renderCell: (params) => <EstadoBadge estado={params.value} />
            },
            {
                field: 'almacenesAsignados',
                headerName: 'Almacenes',
                width: 140,
                renderCell: (params) => {
                    const almacenes = params.row.almacenesAsignados || [];
                    if (almacenes.length === 0) {
                        return (
                            <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, color: "#6c757d" }}>
                                Sin asignar
                            </Typography>
                        );
                    }
                    return (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                            {almacenes.map((almacen) => {
                                const colorAlmacen = getAlmacenColor(almacen);
                                return (
                                    <Chip
                                        key={almacen}
                                        label={getNombreAlmacen(almacen) || almacen}
                                        size="small"
                                        sx={getBadgeStyles(colorAlmacen, { height: 22, fontSize: 10 })}
                                    />
                                );
                            })}
                        </Box>
                    );
                }
            },
            {
                field: 'reponedor',
                headerName: 'Asignado',
                width: 100,
                valueGetter: (params) => {
                    return params.row.reponedor?.username || 'Sin asignar';
                },
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontSize: DATA_GRID_FONT_SIZE.CELDA,
                            color: params.value === 'Sin asignar' ? "#6c757d" : "#495057",
                        }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: 'fechaAsignacion',
                headerName: 'F. asignado',
                width: 130,
                valueFormatter: (params) => params.value ? DateUtils.formatearFechaHora(params.value) : '-'
            },
            {
                field: 'fechaPreparado',
                headerName: 'F. preparado',
                width: 130,
                valueFormatter: (params) => params.value ? DateUtils.formatearFechaHora(params.value) : '-'
            },
            {
                field: 'creado',
                headerName: 'F. creación',
                width: 130,
                valueFormatter: (params) => DateUtils.formatearFechaHora(params.value)
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: 'Acciones',
                width: 100,
                sortable: false,
                filterable: false,
                getActions: ({ id, row }) => {
                    const estaEnProceso = processingId === id;

                    if (row.estado !== PETICION_ESTADO.NO_ATENDIDO.value) {
                        return [];
                    }

                    return [
                        <GridActionsCellItem
                            key="eliminar"
                            icon={estaEnProceso ? <CircularProgress size={16} /> : <DeleteIcon />}
                            label="Eliminar"
                            onClick={() => onEliminar(id)}
                            disabled={estaEnProceso}
                            color="error"
                            title="Eliminar esta petición"
                        />
                    ];
                }
            },
];
